import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { renderInline } from '@evi/ui';
import { manualPageBySlugForLocale, type ManualPage } from '@evi/content';
import { useHelpAudience } from '@/hooks/useHelpAudience';
import { markEvie } from './helpFormat';
import styles from './HelpPage.module.css';

interface Props {
  slug: string;
  onNavigate: (path: string) => void;
}

/**
 * A single manual page, reached at `/help/<slug>` (the route EVII deep-links
 * to by name). Renders the section body plus cross-links to related
 * sections. 'fleet' and 'host' pages are gated: a user who reaches one they
 * are not eligible for by direct link or deep-link sees an explainer, not the
 * content, so the gate holds at the page as well as in the table of contents.
 */
export function HelpSectionPage({ slug, onNavigate }: Props) {
  const { t, i18n } = useTranslation('help');
  // Localized text overlay per key (English fallback); slug identity
  // and audience gating stay single-source.
  const page = manualPageBySlugForLocale(slug, i18n.language);
  const { canSee } = useHelpAudience();

  // Unknown slug — send the driver back to the table of contents rather
  // than a dead end. (Mistyped URL, or a renamed/removed section.)
  if (!page) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button type="button" className={styles.backFab} onClick={() => onNavigate('/help')} aria-label={t('backToHelp')}>
            <ArrowLeft size={18} />
          </button>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{t('notFound.title')}</h1>
            <p className={styles.subtitle}>{t('notFound.subtitle')}</p>
          </div>
        </header>
        <article className={styles.card}>
          <p className={styles.cardBody}>
            {t('notFound.body')}
          </p>
        </article>
      </div>
    );
  }

  const gated = !canSee(page.audience);
  // Audience-specific explainer copy for the gated state. Only 'fleet' and
  // 'host' pages ever gate; 'driver' pages are visible to everyone.
  const audienceLabel = page.audience === 'fleet' ? t('audience.fleet') : t('audience.host');

  // Related sections the viewer is allowed to see.
  const related: ManualPage[] = page.related
    .map((s) => manualPageBySlugForLocale(s, i18n.language))
    .filter((p): p is ManualPage => Boolean(p))
    .filter((p) => canSee(p.audience));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button type="button" className={styles.backFab} onClick={() => onNavigate('/help')} aria-label={t('backToHelp')}>
          <ArrowLeft size={18} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{markEvie(page.title)}</h1>
          <p className={styles.subtitle}>{page.summary}</p>
        </div>
      </header>

      {gated ? (
        <article className={styles.card}>
          <h2 className={styles.cardTitle}>{t('gated.title', { audience: audienceLabel })}</h2>
          <p className={styles.cardBody}>
            {t('gated.body', { audience: audienceLabel })}
          </p>
        </article>
      ) : (
        <article className={styles.card}>
          {/* Bodies may carry blank-line paragraph breaks; render one
              <p> per paragraph so multi-paragraph pages read as written.
              Single-paragraph stubs split to one element, unchanged. */}
          {page.body.split(/\n\n+/).map((para) => (
            <p key={para.slice(0, 40)} className={styles.cardBody}>{renderInline(markEvie(para))}</p>
          ))}
        </article>
      )}

      {!gated && related.length > 0 && (
        <nav className={styles.related} aria-label={t('related.aria')}>
          <h2 className={styles.relatedHeading}>{t('related.heading')}</h2>
          {related.map((p) => (
            <button
              key={p.slug}
              type="button"
              className={styles.relatedLink}
              onClick={() => onNavigate(`/help/${p.slug}`)}
            >
              {markEvie(p.title)}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
